import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import breakpoints from '../../../design-system/breakpoints';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import { SubPageModel } from '../../../common/model/ui/sub-page.model';
import { NavLink, useLocation } from 'react-router-dom';
import { useRouteUrl } from '../../../hooks/use-route-url';
import { useTranslation } from 'react-i18next';

const OvSideNav: FC<{ subPages: SubPageModel[] }> = ({ subPages }) => {
  const { t } = useTranslation();
  const url = useRouteUrl();
  const { pathname } = useLocation();
  const sideNavRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    sideNavRef.current
      ?.querySelector('.activeLink')
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [pathname]);

  const navigationList = subPages.map((page) => {
    const listItemContent = (
      <>
        <StyledListItemIcon>{page.linkIcon}</StyledListItemIcon>
        <ListItemText>
          <Typography textTransform="none" variant="button">
            {page.linkLabel}
          </Typography>
        </ListItemText>
      </>
    );

    const navigationItem = page.shouldOpenDialog ? (
      <StyledListItemButton onClick={page.handleDialogOpen}>
        {listItemContent}
      </StyledListItemButton>
    ) : (
      <StyledListItemButton>{listItemContent}</StyledListItemButton>
    );

    return (
      <NavLinkWrapper key={page.url}>
        {page.shouldOpenDialog ? (
          navigationItem
        ) : (
          <NavLink
            end={!page.url}
            key={page.url}
            to={`${url}${page.url}`}
            className={({ isActive }) => (isActive ? 'activeLink' : '')}
          >
            {navigationItem}
          </NavLink>
        )}
      </NavLinkWrapper>
    );
  });

  return (
    <SideNav
      ref={sideNavRef}
      aria-labelledby={t('app.subPages.menu.ariaLabel')}
    >
      {navigationList}
    </SideNav>
  );
};

export default OvSideNav;

const NavLinkWrapper = styled.li`
  list-style-type: none;

  a {
    text-decoration: none;
  }

  .activeLink {
    .MuiListItemButton-root {
      background-color: ${Colours.OV_BASE};
      border-radius: ${Variables.borderRadius.SMALL};

      &:hover {
        background-color: ${Colours.OV_BASE_HOVER};
      }
    }

    .MuiSvgIcon-root,
    .MuiTypography-button {
      color: ${Colours.OV_SEMI_LIGHT};
    }
  }
`;

const SideNav = styled(List)`
  && {
    display: none;
    box-sizing: border-box;
    padding-top: 0;
    padding-bottom: 0;
    width: 12.75rem;

    @media (min-width: ${breakpoints.lg}) {
      display: block;
      flex: none;
      position: sticky;
      top: 5.25rem;
      max-height: calc(100vh - 6.25rem);
      overflow-x: hidden;
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-width: thin;

      .MuiListItemButton-root {
        width: auto;
      }
    }
  }
`;

const StyledListItemButton = styled(ListItemButton)`
  && {
    padding: 8px 10px;
    width: 12.75rem;
    box-sizing: border-box;

    @media (min-width: ${breakpoints.lg}) {
      border-radius: ${Variables.borderRadius.SMALL};
    }

    &:hover {
      background-color: ${Colours.OV_WHITE_HOVER};
    }
  }
`;

const StyledListItemIcon = styled(ListItemIcon)`
  && {
    min-width: 32px;
    color: ${Colours.OV_BASE};
  }
`;
